import React from 'react';
import PropTypes from 'prop-types';
import { commitMutation, graphql } from 'react-relay';
import { Analytics, GlobalNotifications, Intent, MobileModal } from '../Modules';
import { FloatingLabelInput as Input } from '../Elements';
import { Button, Header, Modal } from 'semantic-ui-react';
import { AppConsumer } from '../App/AppComponent';
import documentStyles from './Document.scss';
import Formsy from 'formsy-react';

export default class RenameDocument extends React.Component {
  static propTypes = {
    documentId: PropTypes.string.isRequired,
    documentType: PropTypes.string.isRequired,
    initialTitle: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    relay: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      title: this.props.initialTitle
    };
    this.titleChangeHandler = this.titleChangeHandler.bind(this);
    this.onValidSubmit = this.onValidSubmit.bind(this);
    this.onInvalidSubmit = this.onInvalidSubmit.bind(this);
  }

  onValidSubmit() {
    this.setState({ loading: true });
    this.updateTitle(
      this.props.documentId,
      this.props.documentType,
      this.state.title
    );
  }

  onInvalidSubmit() {
    GlobalNotifications.show({
      message: 'Unable to rename',
      intent: Intent.DANGER
    });
  }

  titleChangeHandler(e) {
    this.setState({ title: e.target.value });
  }

  updateTitle(documentId, documentType, title) {
    const onCompleted = () => {
      this.setState({ loading: false });
      this.props.onClose();
    };
    switch (documentType) {
      case 'Contract':
        this.updateContractTitle(documentId, title, onCompleted, onCompleted);
        break;
      case 'Estimate':
        this.updateEstimateTitle(documentId, title, onCompleted, onCompleted);
        break;
      case 'Invoice':
        this.updateInvoiceTitle(documentId, title, onCompleted, onCompleted);
        break;
      case 'Form':
        this.updateFormTitle(documentId, title, onCompleted, onCompleted);
        break;
      case 'MusicPlanner':
        this.updateMusicPlannerTitle(documentId, title, onCompleted, onCompleted);
        break;
      case 'Wupload':
        this.updateWuploadName(documentId, title, onCompleted, onCompleted);
        break;
      default:
        return;
    }
  }


  updateContractTitle(id, title, successCb, failureCb) {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to update contract title',
        intent: Intent.DANGER
      });
      if (failureCb) failureCb();
    };
    const onCompleted = () => {
      GlobalNotifications.show({
        message: 'Successfully updated contract title',
        intent: Intent.SUCCESS
      });
      if (successCb) successCb();
      Analytics.trackEvent('updated-contract-title');
    };

    const mutation = graphql`
      mutation RenameDocument_UpdateContractMutation(
        $input: UpdateContractInput!
      ) {
        updateContract(input: $input) {
          contract {
            id
            title
          }
        }
      }
    `;

    const optimisticResponse = {
      updateContract: { contract: { id, title } }
    };

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id,
          title
        }
      },
      optimisticResponse,
      onCompleted,
      onError
    });
  }

  updateEstimateTitle(id, title, successCb, failureCb) {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to update estimate title',
        intent: Intent.DANGER
      });
      if (failureCb) failureCb();
    };
    const onCompleted = () => {
      GlobalNotifications.show({
        message: 'Successfully updated estimate title',
        intent: Intent.SUCCESS
      });
      if (successCb) successCb();
      Analytics.trackEvent('updated-estimate-title');
    };

    const mutation = graphql`
      mutation RenameDocument_UpdateEstimateMutation(
        $input: UpdateEstimateInput!
      ) {
        updateEstimate(input: $input) {
          estimate {
            id
            title
          }
        }
      }
    `;

    const optimisticResponse = {
      updateEstimate: { estimate: { id, title } }
    };

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id,
          title
        }
      },
      optimisticResponse,
      onCompleted,
      onError
    });
  }

  updateInvoiceTitle(id, title, successCb, failureCb) {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to update invoice title',
        intent: Intent.DANGER
      });
      if (failureCb) failureCb();
    };
    const onCompleted = () => {
      GlobalNotifications.show({
        message: 'Successfully updated invoice title',
        intent: Intent.SUCCESS
      });
      if (successCb) successCb();
      Analytics.trackEvent('updated-invoice-title');
    };

    const mutation = graphql`
      mutation RenameDocument_UpdateInvoiceMutation(
        $input: UpdateInvoiceInput!
      ) {
        updateInvoice(input: $input) {
          invoice {
            id
            title
          }
        }
      }
    `;

    const optimisticResponse = {
      updateInvoice: { invoice: { id, title } }
    };

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id,
          title
        }
      },
      optimisticResponse,
      onCompleted,
      onError
    });
  }

  updateFormTitle(id, title, successCb, failureCb) {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to update form title',
        intent: Intent.DANGER
      });
      if (failureCb) failureCb();
    };
    const onCompleted = () => {
      GlobalNotifications.show({
        message: 'Successfully updated form title',
        intent: Intent.SUCCESS
      });
      if (successCb) successCb();
      Analytics.trackEvent('updated-form-title');
    };

    const mutation = graphql`
      mutation RenameDocument_UpdateFormMutation(
        $input: UpdateFormInput!
      ) {
        updateForm(input: $input) {
          form {
            id
            title
          }
        }
      }
    `;

    const optimisticResponse = {
      updateForm: { form: { id, title } }
    };

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id,
          title
        }
      },
      optimisticResponse,
      onCompleted,
      onError
    });
  }

  updateMusicPlannerTitle(id, title, successCb, failureCb) {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to update music list title',
        intent: Intent.DANGER
      });
      if (failureCb) failureCb();
    };
    const onCompleted = () => {
      GlobalNotifications.show({
        message: 'Successfully updated music list title',
        intent: Intent.SUCCESS
      });
      if (successCb) successCb();
      Analytics.trackEvent('updated-music-planner-title');
    };

    const mutation = graphql`
      mutation RenameDocument_UpdateMusicPlannerMutation(
        $input: UpdateMusicPlannerInput!
      ) {
        updateMusicPlanner(input: $input) {
          music_planner {
            id
            title
          }
        }
      }
    `;

    const optimisticResponse = {
      updateMusicPlanner: { music_planner: { id, title } }
    };

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id,
          title
        }
      },
      optimisticResponse,
      onCompleted,
      onError
    });
  }

  updateWuploadName(id, name, successCb, failureCb) {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to update file name',
        intent: Intent.DANGER
      });
      if (failureCb) failureCb();
    };
    const onCompleted = () => {
      GlobalNotifications.show({
        message: 'Successfully updated file name',
        intent: Intent.SUCCESS
      });
      if (successCb) successCb();
      Analytics.trackEvent('updated-wupload-name');
    };

    const mutation = graphql`
      mutation RenameDocument_UpdateWuploadMutation(
        $input: UpdateWuploadInput!
      ) {
        updateWupload(input: $input) {
          wupload {
            id
            name
          }
        }
      }
    `;

    const optimisticResponse = {
      updateWupload: { wupload: { id, name } }
    };

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id,
          name
        }
      },
      optimisticResponse,
      onCompleted,
      onError
    });
  }

  render() {
    const { documentType, onClose } = this.props;
    const { loading } = this.state;
    const typeToLabel = {
      Contract: 'Contract',
      Estimate: 'Estimate',
      Invoice: 'Invoice',
      Form: 'Form',
      MusicPlanner: 'Music List',
      Wupload: 'File Upload'
    };

    const form = (
      <Formsy.Form
        onValidSubmit={this.onValidSubmit}
        onInvalidSubmit={this.onInvalidSubmit}
        ref='form'
      >
        <Input
          name='title'
          type='text'
          label='Name'
          onChange={this.titleChangeHandler}
          value={this.state.title}
          required
          validationError='Name cannot be blank'
        />
      </Formsy.Form>
    );

    const saveText = loading ? 'Saving...' : 'Save';

    return (
      <AppConsumer>
        {state =>
          <div>
            {state.isComputer &&
              <Modal open onClose={onClose} size='small'>
                <Modal.Header content={`Rename ${typeToLabel[documentType]}`} />
                <Modal.Content>
                  {form}
                </Modal.Content>
                <Modal.Actions>
                  <a
                    onClick={onClose}
                    className={documentStyles.modalSecondaryAction}
                  >
                    Cancel
                  </a>
                  <Button
                    primary
                    type='submit'
                    disabled={loading}
                    onClick={() => this.refs.form.submit()}
                  >
                    {saveText}
                  </Button>
                </Modal.Actions>
              </Modal>}
            {!state.isComputer &&
              <MobileModal open onClose={onClose} inline>
                <MobileModal.Header>
                  <div className='menu'>
                    <a className='menu item' onClick={onClose}>
                      Cancel
                    </a>
                  </div>
                </MobileModal.Header>
                <MobileModal.Content>
                  {form}
                  <Button
                    primary
                    onClick={() => this.refs.form.submit()}
                    content={saveText}
                    disabled={loading}
                  />
                </MobileModal.Content>
              </MobileModal>}
          </div>}
      </AppConsumer>
    );
  }
}



// WEBPACK FOOTER //
// ./webpack/components/Document/RenameDocument.js